import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { EmojiEmotions, Translate as TranslateIcon } from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import EmojiPicker from "emoji-picker-react";

const SMSComponent = ({ chatDetail, handleSendTwilioSms, text, setText }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [translateAnchorEl, setTranslateAnchorEl] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState("es");
  const [translatedText, setTranslatedText] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);

  const userId = parseInt(localStorage.getItem("UID"), 10);

  // Emoji handlers
  const handleEmojiClick = (emojiObject) => {
    setText((prevMessage) => prevMessage + emojiObject.emoji);
    handleCloseEmojiPicker();
  };
  const handleEmojiIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseEmojiPicker = () => {
    setAnchorEl(null);
  };

  // Key press handler for SMS send
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendTwilioSms();
    }
  };

  // Translate popover handlers
  const handleTranslateIconClick = (event) => {
    setTranslateAnchorEl(event.currentTarget);
    setTranslatedText(""); // reset any previous translation
  };
  const handleCloseTranslatePopover = () => {
    setTranslateAnchorEl(null);
    setTranslatedText("");
  };

  const isEmojiPickerOpen = Boolean(anchorEl);
  const isTranslatePopoverOpen = Boolean(translateAnchorEl);

  // Handle target language change
  const handleTargetLanguageChange = (event) => {
    setTargetLanguage(event.target.value);
  };

  // Function to call the translation API
  const handleTranslate = async () => {
    if (!text) return;
    setIsTranslating(true);
    try {
      // Replace with your actual API endpoint and adjust the request as needed
      const response = await fetch("/api/translate/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, target_language: targetLanguage }),
      });
      if (!response.ok) {
        throw new Error("Translation API error");
      }
      const data = await response.json();
      setTranslatedText(data.translated_text);
    } catch (error) {
      console.error("Translation error:", error);
      setTranslatedText("Error in translation.");
    } finally {
      setIsTranslating(false);
    }
  };

  // Function to send the translated message
  const handleSendTranslatedMessage = () => {
    // Here you can choose to send the translatedText or update your state accordingly
    // For example, you might want to update `text` and then call your send function:
    setText(translatedText);
    handleSendTwilioSms();
    handleCloseTranslatePopover();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          border: "1px solid #ddd",
          borderRadius: "8px",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          {chatDetail?.map((msg, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection:
                  msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                    ? "row-reverse"
                    : "row",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              {msg?.profile_pic ? (
                <Avatar
                  src={msg?.profile_pic}
                  alt="Profile Image"
                  sx={{
                    width: 38.71,
                    height: 37.66,
                    marginRight:
                      msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                        ? 0
                        : "8px",
                    marginLeft:
                      msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                        ? "8px"
                        : 0,
                    cursor: "pointer",
                  }}
                />
              ) : (
                <Avatar sx={{ width: 40, height: 40 }}>
                  {msg.userName ? msg.userName.charAt(0) : "A"}
                </Avatar>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                      ? "end"
                      : "start",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ textAlign: "right" }}
                >
                  {`${msg?.userName}`} •{" "}
                  {msg?.timestamp?.split("T")[0]}{" "}
                  {msg?.timestamp?.split("T")[1]?.split(".")[0]}
                </Typography>
                <Box
                  sx={{
                    backgroundColor:
                      msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                        ? theme.palette.custom.layoutBgColor
                        : "#F5F5F5",
                    color:
                      msg?.userType === "Admin" || msg?.userType === "SubAdmin"
                        ? theme.palette.custom.layoutFontColor
                        : "#000",
                    padding: "8px 12px",
                    borderRadius: "12px",
                    marginTop: "4px",
                    whiteSpace: "pre-wrap",
                    maxWidth: "750px",
                    fontSize: "14px",
                  }}
                >
                  {msg.message}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Input area */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            borderTop: "1px solid #ddd",
            gap: "8px",
          }}
        >
          <IconButton onClick={handleEmojiIconClick}>
            <EmojiEmotions />
          </IconButton>
            <Button
              variant="contained"
              onClick={handleTranslateIconClick}
              sx={{
                color: theme.palette.custom.layoutFontColor,
                backgroundColor: theme.palette.custom.layoutBgColor,
                width: "200px",
                "&:hover": {
                  backgroundColor: theme.palette.custom.layoutBgColor,
                },
              }}
            >
              Translate
            </Button>
          <Popover
            open={isEmojiPickerOpen}
            anchorEl={anchorEl}
            onClose={handleCloseEmojiPicker}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </Popover>
          <Box sx={{ position: "relative", flex: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="off"
              size="small"
              placeholder="Type a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyPress}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSendTwilioSms}
            sx={{
              color: theme.palette.custom.layoutFontColor,
              backgroundColor: theme.palette.custom.layoutBgColor,
              width: "200px",
              "&:hover": {
                backgroundColor: theme.palette.custom.layoutBgColor,
              },
            }}
          >
            Send
          </Button>
        </Box>
      </Box>

      {/* Translate Popover */}
      <Popover
        open={isTranslatePopoverOpen}
        anchorEl={translateAnchorEl}
        onClose={handleCloseTranslatePopover}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          {/* Source language display */}
          <Typography variant="subtitle2" gutterBottom>
            Source Language: English
          </Typography>
          {/* Show original text in a disabled TextField */}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled
            value={text}
            sx={{ mb: 2 }}
          />

          {/* Target language selection */}
          {/* <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="target-language-label">Translate Language</InputLabel>
            <Select
              labelId="target-language-label"
              value={targetLanguage}
              label="Translate Language"
              onChange={handleTargetLanguageChange}
            >
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
              <MenuItem value="de">German</MenuItem>
            </Select>
          </FormControl> */}

          {/* Translate action */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleTranslate}
            disabled={isTranslating}
            sx={{ mb: 2 }}
          >
            {isTranslating ? "Translating..." : "Translate"}
          </Button>

          {/* If translation is available, show it with Cancel and Send buttons */}
          {translatedText && (
            <>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                value={translatedText}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={handleCloseTranslatePopover}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSendTranslatedMessage}>
                  Send Translate Message
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default SMSComponent;
